import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Skeleton,
  Paper,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useHistory, useParams } from 'react-router-dom';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import { connect } from 'react-redux';
import { getProfiles, getAvailablePermissions, updateProfilePermissions } from '../../redux/profile/profile.actions';
import { getUser } from '../../redux/user/user.actions';
import {
  getCategoriesWithPermissions,
  labelForPermission,
} from './constants';

function ProfilePermissionsEditPage(props) {
  const { theme } = useContext(ThemeContext);
  const history = useHistory();
  const { profileId } = useParams();
  const id = parseInt(profileId, 10);

  const {
    profiles,
    availablePermissions,
    currentUser,
    getProfiles,
    getAvailablePermissions,
    updateProfilePermissions,
    getUser,
  } = props;

  const profile = (profiles || []).find((p) => p.id === id);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getProfiles(true);
    getAvailablePermissions();
  }, [getProfiles, getAvailablePermissions]);

  useEffect(() => {
    if (Array.isArray(profiles) && Array.isArray(availablePermissions)) setLoaded(true);
  }, [profiles, availablePermissions]);

  useEffect(() => {
    if (profile && Array.isArray(profile.permissions)) {
      setSelectedPermissions([...profile.permissions]);
    }
  }, [profile]);

  const handleToggle = (permission) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission]
    );
  };

  const handleSelectAllInCategory = (perms) => {
    const allSelected = perms.every((p) => selectedPermissions.includes(p));
    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((p) => !perms.includes(p)));
    } else {
      setSelectedPermissions((prev) => [...new Set([...prev, ...perms])]);
    }
  };

  const handleSave = () => {
    setSaving(true);
    updateProfilePermissions(id, selectedPermissions)
      .then(() => {
        if (currentUser?.profile_id === id && getUser) getUser(currentUser.id);
        history.push('/configuracoes?tab=4');
      })
      .finally(() => setSaving(false));
  };

  const handleBack = () => {
    history.push('/configuracoes?tab=4');
  };

  if (!Number.isInteger(id) || id < 1) {
    return (
      <Box sx={{ p: 3, height: '100%' }}>
        <Alert severity="error">Perfil inválido.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/configuracoes?tab=4')} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  if (!loaded && !profile) {
    return (
      <Box sx={{ px: 3, py: 4, height: '100%' }}>
        <Skeleton variant="text" width={280} height={48} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3, height: '100%' }}>
        <Alert severity="warning">Perfil não encontrado.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  const categories = getCategoriesWithPermissions(availablePermissions || []);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
        overflow: 'auto',
      }}
    >
      {/* Header fixo - página inteira */}
      <Paper
        elevation={0}
        square
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: { xs: 2, sm: 3 },
          py: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                textTransform: 'none',
                color: theme.palette.text.secondary,
                flexShrink: 0,
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
            >
              Voltar
            </Button>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Permissões: {profile.name}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {selectedPermissions.length} de {(availablePermissions || []).length} permissões selecionadas
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              flexShrink: 0,
              boxShadow: theme.palette.mode === 'dark' ? undefined : '0 2px 8px rgba(99, 165, 199, 0.35)',
            }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      </Paper>

      {/* Conteúdo em página inteira - categorias */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: { xs: 2, sm: 3 },
          py: 3,
        }}
      >
        {categories.map((category) => {
          const perms = category.permissions;
          const selectedInCategory = perms.filter((p) => selectedPermissions.includes(p)).length;
          const allSelectedInCategory = perms.length > 0 && selectedInCategory === perms.length;

          return (
            <Paper
              key={category.key}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {category.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                      {category.description}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleSelectAllInCategory(perms)}
                    sx={{ textTransform: 'none', flexShrink: 0 }}
                  >
                    {allSelectedInCategory ? 'Desmarcar todas' : 'Marcar todas'}
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 0,
                  p: 2,
                }}
              >
                {perms.map((perm) => (
                  <FormControlLabel
                    key={perm}
                    control={
                      <Checkbox
                        checked={selectedPermissions.includes(perm)}
                        onChange={() => handleToggle(perm)}
                        sx={{ py: 0.5 }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {labelForPermission(perm)}
                      </Typography>
                    }
                    sx={{
                      m: 0,
                      py: 1.5,
                      px: 2,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                      },
                      '& .MuiFormControlLabel-label': { flex: 1 },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  profiles: state.profiles?.profiles ?? [],
  availablePermissions: state.profiles?.availablePermissions ?? [],
  currentUser: state.user?.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  getProfiles: (withPermissions) => dispatch(getProfiles(withPermissions)),
  getAvailablePermissions: () => dispatch(getAvailablePermissions()),
  updateProfilePermissions: (profileId, permissions) => dispatch(updateProfilePermissions(profileId, permissions)),
  getUser: (id) => dispatch(getUser(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfilePermissionsEditPage);
